/**
 * functionAuth.ts: the headers for calling one of our Edge Functions as the
 * signed-in person.
 *
 * The functions that act on a person's data (the guide, signal extraction) work
 * out who is calling from the bearer token, and from nothing else in the request
 * (supabase/functions/_shared/callerAuth.ts, #72). So the token has to be the
 * person's own. These calls used to send the project's public key as the bearer
 * token, which names nobody, and put the user id in the request body instead.
 *
 * `getSession()` returns the current access token and refreshes it first if it
 * is about to expire, so a long session does not start failing mid-way.
 *
 * With no signed-in session (a signed-out tab) the public key is sent, the
 * function answers 401, and the screen shows its "signed out" message.
 */
import { supabase } from './supabase'

export async function functionHeaders(): Promise<Record<string, string>> {
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string
  let token = anonKey
  try {
    const { data } = (await supabase?.auth.getSession()) ?? { data: null }
    token = data?.session?.access_token ?? anonKey
  } catch {
    // Keep the public key: the function will say the caller is not signed in.
  }
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    'apikey': anonKey,
  }
}
